// SPDX-License-Identifier: GPL-3.0-or-later
//
// Structured output shapes the AI is asked to fill. The template renderer
// (latex/render.ts) consumes these and produces .tex. Keeping the schema
// strict here means the AI's job is content, not typesetting.

export interface CvContact {
  email: string;
  phone?: string;
  location?: string;
  links?: { label: string; url: string }[];
}

export interface CvExperience {
  company: string;
  role: string;
  period: string;
  bullets: string[];
}

export interface CvEducation {
  school: string;
  degree: string;
  period: string;
  notes?: string;
}

export interface CvSkills {
  category: string;
  items: string[];
}

export interface CvDoc {
  fullName: string;
  contact: CvContact;
  summary: string;
  experience: CvExperience[];
  education: CvEducation[];
  skills: CvSkills[];
}

export function isCvDoc(x: unknown): x is CvDoc {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  if (typeof o['fullName'] !== 'string') return false;
  if (typeof o['summary'] !== 'string') return false;
  if (!o['contact'] || typeof o['contact'] !== 'object') return false;
  const c = o['contact'] as Record<string, unknown>;
  if (typeof c['email'] !== 'string') return false;
  if (!Array.isArray(o['experience'])) return false;
  if (!Array.isArray(o['education'])) return false;
  if (!Array.isArray(o['skills'])) return false;
  return true;
}

export const CV_JSON_SCHEMA = `{
  "fullName": string,
  "contact": {
    "email": string,
    "phone"?: string,
    "location"?: string,
    "links"?: [{ "label": string, "url": string }]
  },
  "summary": string,          // 2-3 sentences, tuned to the job
  "experience": [{
    "company": string,
    "role": string,
    "period": string,         // e.g. "2022 — present"
    "bullets": string[]       // 3-6 bullets per role, concrete & quantified
  }],
  "education": [{
    "school": string,
    "degree": string,
    "period": string,
    "notes"?: string
  }],
  "skills": [{ "category": string, "items": string[] }]
}`;

// ---------- Cover letter ----------

export interface CoverApplicant {
  fullName: string;
  email: string;
  phone?: string;
  location?: string;
}

export interface CoverRecipient {
  name?: string;
  title?: string;
  company: string;
  address?: string;
}

export interface CoverDoc {
  applicant: CoverApplicant;
  recipient: CoverRecipient;
  date: string;             // ISO date or human "May 19, 2026"
  greeting: string;         // e.g. "Dear Hiring Manager,"
  paragraphs: string[];     // 3-5 paragraphs of body
  signoff: string;          // e.g. "Sincerely,"
}

export function isCoverDoc(x: unknown): x is CoverDoc {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  if (!o['applicant'] || typeof o['applicant'] !== 'object') return false;
  if (!o['recipient'] || typeof o['recipient'] !== 'object') return false;
  if (typeof o['greeting'] !== 'string') return false;
  if (!Array.isArray(o['paragraphs'])) return false;
  if (typeof o['signoff'] !== 'string') return false;
  return true;
}

export const COVER_JSON_SCHEMA = `{
  "applicant": {
    "fullName": string,
    "email": string,
    "phone"?: string,
    "location"?: string
  },
  "recipient": {
    "name"?: string,        // hiring manager name if known
    "title"?: string,
    "company": string,
    "address"?: string
  },
  "date": string,           // e.g. "May 19, 2026"
  "greeting": string,       // e.g. "Dear Hiring Manager," — match the recipient
  "paragraphs": string[],   // 3-5 paragraphs of body, plain text
  "signoff": string         // e.g. "Sincerely,"
}`;

// ---------- Interview prep ----------

export interface InterviewerInfo {
  name: string;
  role?: string;
}

export interface InterviewTopic {
  topic: string;
  sampleQuestions: string[];
}

export interface InterviewDoc {
  interviewer: InterviewerInfo;
  interviewType: string;    // 'screening'|'technical'|'behavioral'|'panel'|'culture'|'final' or free text
  likelyTopics: InterviewTopic[];
  yourQuestions: string[];
  companyTalkingPoints: string[];
  recentNews?: string[];    // model may hallucinate — flagged in the template
}

export function isInterviewDoc(x: unknown): x is InterviewDoc {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  if (!o['interviewer'] || typeof o['interviewer'] !== 'object') return false;
  if (typeof (o['interviewer'] as Record<string, unknown>)['name'] !== 'string') return false;
  if (typeof o['interviewType'] !== 'string') return false;
  if (!Array.isArray(o['likelyTopics'])) return false;
  if (!Array.isArray(o['yourQuestions'])) return false;
  if (!Array.isArray(o['companyTalkingPoints'])) return false;
  return true;
}

// ---------- Auto-screen ----------

export interface ScreenResult {
  match: boolean;
  reason: string;          // 1-2 sentence justification
  signals?: string[];      // optional bullet list of pros/cons
}

export function isScreenResult(x: unknown): x is ScreenResult {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return typeof o['match'] === 'boolean' && typeof o['reason'] === 'string';
}

export const SCREEN_JSON_SCHEMA = `{
  "match": boolean,         // true if the job matches the user's preferences
  "reason": string,         // 1-2 sentence justification (mention the specific signals)
  "signals"?: string[]      // optional short bullet list of pros and cons
}`;

export const INTERVIEW_JSON_SCHEMA = `{
  "interviewer": { "name": string, "role"?: string },
  "interviewType": string,    // screening | technical | behavioral | panel | culture | final | other
  "likelyTopics": [{ "topic": string, "sampleQuestions": string[] }],
  "yourQuestions": string[],
  "companyTalkingPoints": string[],
  "recentNews"?: string[]     // ONLY include if you are highly confident; will be flagged as VERIFY
}`;
